/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN STUDENT FEE PLAN SLICE
 * Role: Admin
 * Scope: Redux state management for student fee plan mapping,
 *        audit history tracking, and correction updates.
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { createSlice } from "@reduxjs/toolkit";
import {
  fetchStudentActiveFeePlan,
  fetchStudentFeePlanHistory,
  saveStudentFeePlan,
  updateStudentFeePlanThunk,
  removeStudentFeePlan,
} from "./student_fee_plan.thunk";

const initialState = {
  activePlan: null,
  history: [],
  loading: false,
  error: null,
};

const studentFeePlanSlice = createSlice({
  name: "studentFeePlan",
  initialState,
  reducers: {
    /**
     * Resets the fee plan state slice to initial values.
     */
    clearFeePlanState: (state) => {
      state.activePlan = null;
      state.history = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ================= FETCH ACTIVE PLAN ================= */
      .addCase(fetchStudentActiveFeePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentActiveFeePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.activePlan = action.payload;
      })
      .addCase(fetchStudentActiveFeePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ================= FETCH PLAN HISTORY ================= */
      .addCase(fetchStudentFeePlanHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentFeePlanHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload;
      })
      .addCase(fetchStudentFeePlanHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ================= SAVE / MAP NEW PLAN ================= */
      .addCase(saveStudentFeePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveStudentFeePlan.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(saveStudentFeePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ================= UPDATE / CORRECT PLAN ================= */
      .addCase(updateStudentFeePlanThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStudentFeePlanThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateStudentFeePlanThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ================= DEACTIVATE PLAN ================= */
      .addCase(removeStudentFeePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeStudentFeePlan.fulfilled, (state) => {
        state.loading = false;
        state.activePlan = null;
      })
      .addCase(removeStudentFeePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearFeePlanState } = studentFeePlanSlice.actions;

export default studentFeePlanSlice.reducer;